/**
 * CallBoardNode Component
 *
 * A React Flow node that enables board composition - calling one board
 * from another as a tool/function. The called board executes and returns
 * its output data to be used in the calling workflow.
 *
 * Features:
 * - Board selector dropdown to choose target board
 * - Version pinning (like npm - pin to specific version)
 * - Input mapping (connect incoming data to board inputs)
 * - Call button to trigger execution
 * - Result display showing returned data
 * - Auto-inserted policy badges (rate limiter)
 *
 * Visual design:
 * - Cyan/blue gradient for call-related actions
 * - Floating header for title editing
 * - Compact policy badges showing security gates
 */

import { memo, useState, useCallback, useEffect } from 'react';
import {
  Handle,
  Position,
  NodeResizer,
  type NodeProps,
  type Node,
} from '@xyflow/react';
import type { CallBoardNodeData } from '@deep-agents/shared-types';
import { BoardSelector } from './BoardSelector';
import { PolicyNodeBadge } from '../../policy/PolicyNodeBadge';

// ============================================================================
// TYPES
// ============================================================================

/**
 * React Flow node type for CallBoardNode.
 */
type CallBoardNodeType = Node<CallBoardNodeData, 'call-board'>;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * CallBoardNode - Board composition node for calling boards as tools.
 *
 * This enables complex workflows by letting boards call other boards
 * as subroutines, similar to function calls in programming.
 *
 * Usage flow:
 * 1. User adds CallBoardNode to canvas
 * 2. User selects target board from dropdown
 * 3. User connects inputs to the node's input handle
 * 4. When workflow runs, the target board executes
 * 5. Result appears on the output handle
 */
export const CallBoardNode = memo(function CallBoardNode({
  id,
  data,
  selected,
  dragging,
}: NodeProps<CallBoardNodeType>) {
  // ========================================
  // Destructure Data
  // ========================================

  const {
    title = 'Call Board',
    targetBoardId,
    targetBoardName,
    versionPin,
    status = 'idle',
    result,
    error,
    durationMs,
    onTitleChange,
    onTargetBoardChange,
    onCall,
    boardId,
  } = data;

  // ========================================
  // Local State
  // ========================================

  // Local title for immediate UI updates
  const [localTitle, setLocalTitle] = useState(title);

  // Hover state for showing floating header
  const [isHovered, setIsHovered] = useState(false);

  // Sync local title with data prop
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // ========================================
  // Handlers
  // ========================================

  /**
   * Handle title changes.
   * Updates local state immediately and notifies parent.
   */
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setLocalTitle(newTitle);
      onTitleChange?.(newTitle);
    },
    [onTitleChange]
  );

  /**
   * Handle board selection from dropdown.
   */
  const handleBoardSelect = useCallback(
    (boardId: string, boardName: string) => {
      onTargetBoardChange?.(boardId, boardName);
    },
    [onTargetBoardChange]
  );

  /**
   * Handle call button click.
   */
  const handleCall = useCallback(() => {
    onCall?.();
  }, [onCall]);

  // ========================================
  // Shadow Style
  // ========================================

  /**
   * Get shadow style based on node state.
   * Uses CSS variables for consistent elevation system.
   */
  const getShadowStyle = () => {
    if (dragging) return 'var(--shadow-elevation-high)';
    if (selected) return 'var(--shadow-elevation-medium)';
    return 'var(--shadow-elevation-low)';
  };

  // Show controls when hovered or selected
  const showControls = isHovered || selected;

  // ========================================
  // Mock Policy (for display)
  // ========================================

  // In production, policies would come from the policy engine
  // based on the board's node types. For now, we show a mock
  // rate limiter since call-board nodes are rate limited.
  const mockPolicy = {
    id: `policy-rate-limiter-${id}`,
    type: 'rate-limiter' as const,
    position: 'before' as const,
    targetNodeId: id,
    config: { maxPerMinute: 10, action: 'block' as const },
    isVisible: true,
    status: 'active' as const,
  };

  // ========================================
  // Render
  // ========================================

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ======================================== */}
      {/* Connection Handles */}
      {/* ======================================== */}

      {/* Input Handle - receives data to pass to target board */}
      <Handle
        type="target"
        position={Position.Left}
        id="inputs"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white"
        style={{ top: '50%' }}
        title="Input data to pass to board"
      />

      {/* Output Handle - outputs result from target board */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-cyan-500 !border-2 !border-white"
        style={{ top: '50%' }}
        title="Board execution result"
      />

      {/* ======================================== */}
      {/* Floating Header (shows on hover/select) */}
      {/* ======================================== */}

      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex items-center gap-2 px-1 nodrag nopan">
          {/* Title Input */}
          <input
            type="text"
            value={localTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Untitled Call"
            className="
              px-2.5 py-1 text-[12px] font-semibold
              bg-gradient-to-r from-cyan-50 to-blue-50
              rounded-lg border border-cyan-100/50
              outline-none focus:border-cyan-300 focus:ring-1 focus:ring-cyan-200
              placeholder:text-cyan-300 text-cyan-700
            "
          />
        </div>
      </div>

      {/* ======================================== */}
      {/* Main Card */}
      {/* ======================================== */}

      <div
        className="
          relative bg-white rounded-2xl overflow-visible
          border border-neutral-200/60 transition-all duration-300
        "
        style={{
          boxShadow: getShadowStyle(),
          minWidth: 320,
          minHeight: 200,
        }}
      >
        {/* Node Resizer (visible when selected) */}
        <NodeResizer
          minWidth={320}
          minHeight={200}
          isVisible={selected}
          lineClassName="border-cyan-500"
          handleClassName="bg-cyan-500 border-2 border-white rounded"
        />

        {/* Card Content */}
        <div className="px-5 pt-5 pb-4">
          {/* ======================================== */}
          {/* Policy Badge - auto-inserted */}
          {/* ======================================== */}

          <div className="mb-3">
            <PolicyNodeBadge policy={mockPolicy} />
          </div>

          {/* ======================================== */}
          {/* Target Board Selector */}
          {/* ======================================== */}

          <div className="mb-4 nodrag nopan">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Target Board
            </label>
            <BoardSelector
              selectedBoardId={targetBoardId}
              onSelect={handleBoardSelect}
            />
            {targetBoardName && (
              <div className="mt-1 text-xs text-gray-500">
                Will execute: <span className="font-medium">{targetBoardName}</span>
              </div>
            )}
          </div>

          {/* ======================================== */}
          {/* Version Pin (optional) */}
          {/* ======================================== */}

          {targetBoardId && (
            <div className="mb-4 text-xs text-gray-400">
              Version: {versionPin || 'latest'}
            </div>
          )}

          {/* ======================================== */}
          {/* Call Button */}
          {/* ======================================== */}

          <div className="nodrag nopan">
            <button
              type="button"
              onClick={handleCall}
              disabled={!targetBoardId || status === 'calling'}
              className={`
                w-full flex items-center justify-center gap-2 px-4 py-2.5
                text-[14px] font-semibold rounded-lg transition-all
                ${
                  !targetBoardId || status === 'calling'
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 shadow-md hover:shadow-lg'
                }
              `}
            >
              {status === 'calling' ? (
                <>
                  {/* Spinner */}
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Executing...
                </>
              ) : (
                <>
                  {/* Play icon */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Call Board
                </>
              )}
            </button>
          </div>

          {/* ======================================== */}
          {/* Result Display (success) */}
          {/* ======================================== */}

          {status === 'complete' && result !== undefined && result !== null && (
            <div className="mt-4 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
              <div className="text-[10px] font-semibold text-cyan-600 uppercase mb-1">
                Result {durationMs && `(${durationMs}ms)`}
              </div>
              <pre className="text-xs text-gray-700 font-mono max-h-20 overflow-auto whitespace-pre-wrap">
                {typeof result === 'string'
                  ? result
                  : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {/* ======================================== */}
          {/* Error Display */}
          {/* ======================================== */}

          {status === 'error' && error && (
            <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="text-[10px] font-semibold text-red-600 uppercase mb-1">
                Error
              </div>
              <div className="text-xs text-red-600">{error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Set display name for React DevTools
CallBoardNode.displayName = 'CallBoardNode';
